import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";

import { ISO_4217_CURRENCY_OPTIONS, PROCUREMENT_STATUS_OPTIONS } from "@/api/suppliers.constraints";
import { useGetActiveDirectoryUsersQuery } from "@/api/users-admin";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { ContractFamilyMetadataRequest, SupplierFamilySummary } from "@/types/supplier";

import {
  familyMetadataValues,
  toContractFamilyMetadataRequest,
  type ContractFamilyMetadataValues,
} from "./contract-family-metadata";

interface Props {
  family?: SupplierFamilySummary;
  initialName?: string;
  submitLabel?: string;
  isSubmitting?: boolean;
  supplierOwnerId: string | null;
  ownerError?: string | null;
  onCancel?: () => void;
  onSubmit: (request: ContractFamilyMetadataRequest) => Promise<void> | void;
}

export const ContractFamilyMetadataForm = ({
  family,
  initialName,
  submitLabel = "Save metadata",
  isSubmitting,
  supplierOwnerId,
  ownerError,
  onCancel,
  onSubmit,
}: Props) => {
  const methods = useForm<ContractFamilyMetadataValues>({
    defaultValues: { ...familyMetadataValues(family), name: family?.name ?? initialName ?? "" },
  });
  const { data: owners = [] } = useGetActiveDirectoryUsersQuery();
  const { organization } = useAuthenticatedUser();
  const divisionOptions = [...new Set([...(organization.contractFamilyDivisions ?? []), ...(family?.divisions ?? [])])];
  const contactMode = methods.watch("contact_mode");
  const total = methods.watch("estimated_total_contract_value");
  const annual = methods.watch("estimated_annual_contract_value");
  const [initialExpiryDate] = React.useState(() => familyMetadataValues(family).expiry_date);
  const requiresExplicitOwner = supplierOwnerId === null;

  React.useEffect(() => {
    if (ownerError) {
      methods.setError("owner_user_id", { type: "server", message: ownerError }, { shouldFocus: true });
    }
  }, [methods, ownerError]);

  return (
    <FormProvider {...methods}>
      <form
        className="space-y-5"
        onSubmit={methods.handleSubmit(values => onSubmit(toContractFamilyMetadataRequest(values, initialExpiryDate)))}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="family-name">Contract Family name</Label>
            <Input id="family-name" maxLength={255} required {...methods.register("name")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="family-external-id">External ID</Label>
            <Input id="family-external-id" maxLength={255} {...methods.register("external_id")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="family-owner">Owner</Label>
            <select
              id="family-owner"
              aria-invalid={Boolean(methods.formState.errors.owner_user_id)}
              aria-describedby={methods.formState.errors.owner_user_id ? "family-owner-error" : undefined}
              className="h-10 w-full rounded-md border bg-white px-3 text-sm"
              {...methods.register("owner_user_id", {
                validate: value => !requiresExplicitOwner || Boolean(value) || "Select an active Contract Family owner",
              })}
            >
              <option value="" disabled={requiresExplicitOwner}>
                {requiresExplicitOwner ? "Supplier has no owner — select below" : "Inherit Supplier owner"}
              </option>
              {owners.map(owner => (
                <option key={owner.id} value={owner.id}>
                  {[owner.name, owner.surname].filter(Boolean).join(" ") || owner.email}
                </option>
              ))}
            </select>
            {methods.formState.errors.owner_user_id ? (
              <p id="family-owner-error" role="alert" className="text-xs text-red-600">
                {methods.formState.errors.owner_user_id.message}
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="family-total-value">Estimated total contract value</Label>
            <Input
              id="family-total-value"
              inputMode="decimal"
              placeholder="0.00"
              {...methods.register("estimated_total_contract_value", { pattern: /^\d+(\.\d{1,2})?$/ })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="family-annual-value">Estimated annual contract value</Label>
            <Input
              id="family-annual-value"
              inputMode="decimal"
              placeholder="0.00"
              {...methods.register("estimated_annual_contract_value", { pattern: /^\d+(\.\d{1,2})?$/ })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="family-currency">Currency</Label>
            <select
              id="family-currency"
              required={Boolean(total || annual)}
              className="h-10 w-full rounded-md border bg-white px-3 text-sm"
              {...methods.register("currency_code")}
            >
              <option value="">Select currency</option>
              {ISO_4217_CURRENCY_OPTIONS.map(currency => (
                <option key={currency}>{currency}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="family-status">Status</Label>
            <select
              id="family-status"
              className="h-10 w-full rounded-md border bg-white px-3 text-sm capitalize"
              {...methods.register("procurement_status")}
            >
              <option value="">Not set</option>
              {PROCUREMENT_STATUS_OPTIONS.map(status => (
                <option key={status}>{status}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="family-end-date">Contract end date</Label>
            <Input
              id="family-end-date"
              type="date"
              aria-describedby="family-end-date-hint"
              {...methods.register("expiry_date")}
            />
          </div>
          <p id="family-end-date-hint" className="text-xs text-gray-500 sm:col-span-2">
            The Contract Summary keeps its own 00.10 Current Term End Date. Clear this date to use that one.
          </p>
        </div>

        <fieldset>
          <legend className="text-sm font-medium">Divisions</legend>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {divisionOptions.map(division => (
              <label key={division} className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                <input type="checkbox" value={division} {...methods.register("divisions")} /> {division}
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset className="space-y-3">
          <legend className="text-sm font-medium">Contact details</legend>
          <select
            aria-label="Contact source"
            className="h-10 w-full rounded-md border bg-white px-3 text-sm"
            {...methods.register("contact_mode")}
          >
            <option value="inherited">Inherit Supplier contact</option>
            <option value="custom">Use custom contact</option>
          </select>
          {contactMode === "custom" ? (
            <div className="grid gap-3 sm:grid-cols-3">
              <Input
                aria-label="Contact name"
                maxLength={255}
                placeholder="Name"
                {...methods.register("contact_name")}
              />
              <Input
                aria-label="Contact email"
                type="email"
                maxLength={320}
                placeholder="Email"
                {...methods.register("contact_email")}
              />
              <Input
                aria-label="Contact phone"
                maxLength={64}
                placeholder="Phone"
                {...methods.register("contact_phone")}
              />
            </div>
          ) : null}
        </fieldset>

        <div className="flex justify-end gap-2">
          {onCancel ? (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          ) : null}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving…" : submitLabel}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
